import jss from 'jss';
import preset from 'jss-preset-default';
import {
	BLACK,
	ColorPalette2022Branding,
	ColorPaletteBlue,
	ColorPaletteGrey,
	ColorPaletteOrange,
	ColorPaletteRed,
	WHITE,
} from './theme.palette';

// Use the internal rule names for all classNames
const createGenerateClassName = () => (rule) => rule.key;

const options = preset();
options.createGenerateClassName = createGenerateClassName;
jss.setup(options);

export function rgba(color: string, alpha?: number) {
	if (alpha == null) {
		return `rgb(${color})`;
	}

	return `rgba(${color}, ${alpha})`;
}

export function assetURL(path: string) {
	return `url(${assetPath(path)})`;
}

export function assetPath(path: string) {
	const version = typeof window !== 'undefined' ? (window as any).IKE_APPLICATION_VERSION : undefined;
	if (version == null || version === '0000-dev') {
		return path;
	}
	// Strip leading slash from path to avoid double-slash when joining with version prefix
	const stripped = path.startsWith('/') ? path.slice(1) : path;
	return `/${version}/${stripped}`;
}

export const DefaultTheme = {
	space: (amount: number) => DefaultTheme.spacingFactor * amount + 'px',
	spacingFactor: 4,
	color: {
		primary: rgba(ColorPaletteBlue.C700),
		primaryDark: rgba(ColorPaletteBlue.C900),
		accent: ColorPalette2022Branding.ikeOrange,
		accentDark: rgba(ColorPaletteOrange.C900),
		lightIndigo: ColorPalette2022Branding.lightIndigo,
	},
	background: {
		darkGrey: ColorPalette2022Branding.darkGray,
		mediumGrey: '#3C404D',
		offWhite: '#fafafa',
		grey: rgba(ColorPaletteGrey.C100),
		lightGrey: rgba(BLACK, 0.06),
		active: rgba(BLACK, 0.12),
		fadedGrey: '#b8b8b8',
	},
	font: {
		size: 14,
		family: 'Roboto, sans-serif',
		familyCondensed: 'Roboto Condensed',
		weight: {
			normal: 400,
			bold: 700,
		},
		color: {
			white: rgba(WHITE, 0.87),
			primary: '#000000',
			supporting: rgba(BLACK, 0.54),
			disabled: rgba(BLACK, 0.26),
			divider: rgba(BLACK, 0.12),
			hover: rgba(BLACK, 0.2),
			link: rgba(ColorPaletteOrange.C700, 0.87),
			error: rgba(ColorPaletteRed.A700),
			insight: '#fff',
		},
	},
	shadow: {
		box: {
			bottom: '-2px 4px 2px -3px rgba(0, 0, 0, .14)',
			dp2: '0 2px 2px 0 rgba(0, 0, 0, .14), 0 3px 1px -2px rgba(0, 0, 0, .2), 0 1px 5px 0 rgba(0, 0, 0, .12)',
			dp2w5: '0 0 0 5px rgba(0,0,0,.12), 0 0 0 5px rgba(0,0,0,.24)',
			dp4: '0 4px 5px 0 rgba(0, 0, 0, .14), 0 1px 10px 0 rgba(0, 0, 0, .12), 0 2px 4px -1px rgba(0, 0, 0, .2)',
			dp6: '0 6px 10px 0 rgba(0, 0, 0, .14), 0 1px 18px 0 rgba(0, 0, 0, .12), 0 3px 5px -1px rgba(0, 0, 0, .2)',
		},
		text: `0 1px 1px ${rgba(BLACK, 0.54)}`,
	},
};

export const BaseStyle = {
	'@global': {
		a: {
			color: DefaultTheme.color.accent,
			'font-weight': 500,
			'text-decoration': 'none',
			cursor: 'pointer',
		},
	},
	'mdl-button--accent.mdl-button--accent.mdl-button--fab, .mdl-button--accent.mdl-button--accent.mdl-button--raised': {
		color: WHITE,
		'background-color': DefaultTheme.color.accent,
	},
	'mdl-button--accent.mdl-button--accent': {
		color: DefaultTheme.color.accent,
	},
	'mdl-button--raised.mdl-button--disabled.mdl-button--disabled, .mdl-button--raised[disabled][disabled]': {
		'background-color': DefaultTheme.font.color.divider,
		color: DefaultTheme.font.color.disabled,
		'box-shadow': 'none',
	},
	'@media print': {
		body: {
			background: 'transparent !important',
		},
		'*': {
			color: '#000 !important',
			'box-shadow': 'none !important',
			'text-shadow': 'none !important',
		},
	},
};

const baseSheet = jss.createStyleSheet(BaseStyle);
export function addStyles(style) {
	baseSheet.addRules(style);
}

export function attachSheet() {
	baseSheet.attach();
}
